import React, { createContext, useContext, useState, useCallback } from 'react';
import ConfirmationModal from './ConfirmationModal';

const ConfirmationContext = createContext(null);

/**
 * Provider component that wraps your app to enable confirmation modals
 *
 * Usage:
 * <ConfirmationProvider>
 *   <App />
 * </ConfirmationProvider>
 */
export const ConfirmationProvider = ({ children }) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    variant: 'default',
    showCancelButton: true,
    closeOnOverlayClick: true,
    onConfirm: null,
    onCancel: null
  });

  /**
   * Show confirmation modal and return a promise
   *
   * @param {Object} config
   * @param {string} config.title - Modal title
   * @param {string} config.message - Modal message
   * @param {string} config.confirmText - Text for confirm button (default: "Confirm")
   * @param {string} config.cancelText - Text for cancel button (default: "Cancel")
   * @param {string} config.variant - Visual variant: 'danger', 'warning', 'info', 'default' (default: 'default')
   * @param {boolean} config.showCancelButton - Show cancel button (default: true)
   * @param {boolean} config.closeOnOverlayClick - Close on overlay click (default: true)
   * @returns {Promise<boolean>} - Resolves to true if confirmed, false if cancelled
   */
  const confirm = useCallback((config) => {
    return new Promise((resolve) => {
      setModalState({
        isOpen: true,
        title: config.title || 'Confirm Action',
        message: config.message || 'Are you sure?',
        confirmText: config.confirmText || 'Confirm',
        cancelText: config.cancelText || 'Cancel',
        variant: config.variant || 'default',
        showCancelButton: config.showCancelButton !== undefined ? config.showCancelButton : true,
        closeOnOverlayClick: config.closeOnOverlayClick !== undefined ? config.closeOnOverlayClick : true,
        onConfirm: () => {
          setModalState((prev) => ({ ...prev, isOpen: false }));
          resolve(true);
        },
        onCancel: () => {
          setModalState((prev) => ({ ...prev, isOpen: false }));
          resolve(false);
        }
      });
    });
  }, []);

  return (
    <ConfirmationContext.Provider value={{ confirm }}>
      {children}
      <ConfirmationModal
        isOpen={modalState.isOpen}
        title={modalState.title}
        message={modalState.message}
        confirmText={modalState.confirmText}
        cancelText={modalState.cancelText}
        variant={modalState.variant}
        showCancelButton={modalState.showCancelButton}
        closeOnOverlayClick={modalState.closeOnOverlayClick}
        onConfirm={modalState.onConfirm}
        onCancel={modalState.onCancel}
      />
    </ConfirmationContext.Provider>
  );
};

/**
 * Custom hook to use confirmation modal
 *
 * @returns {Object} - Object with confirm function
 *
 * @example
 * const { confirm } = useConfirmation();
 *
 * // Basic usage
 * const confirmed = await confirm({
 *   title: 'Delete Item',
 *   message: 'Are you sure you want to delete this item?'
 * });
 *
 * if (confirmed) {
 *   // User clicked confirm
 * }
 *
 * // With danger variant
 * const confirmed = await confirm({
 *   title: 'Delete Project',
 *   message: 'This action cannot be undone.',
 *   variant: 'danger',
 *   confirmText: 'Delete'
 * });
 */
export const useConfirmation = () => {
  const context = useContext(ConfirmationContext);

  if (!context) {
    throw new Error('useConfirmation must be used within a ConfirmationProvider');
  }

  return context;
};
